import type { Axis } from 'highcharts/highcharts.src';
import type { ChartRenderCallbackFunction, Options } from 'highcharts/highstock';
import Highcharts from 'highcharts/highstock';
import moment from 'moment/moment';
import { chartColors, CLIENT_OUTPUT_Y_AXIS_OFFSET, FONT_FAMILY_VERLAG, X_AXIS_OFFSET } from '../../../config/constants';
import type { HighchartCallbackFn, IClientPackageMonteCarloParams } from '../../../interfaces/projection';

(function (H: typeof Highcharts) {
    // @ts-expect-error Problems with missing Highcharts types
    H.wrap(H['seriesTypes'].spline.prototype, 'drawLegendSymbol', function (this: Highcharts.Chart, proceed) {
        // @ts-expect-error Problems with missing Highcharts types
        if (this.userOptions['customShowRectSymbol'] === true) {
            // @ts-expect-error Problems with missing Highcharts types
            H['seriesTypes'].column.prototype.drawLegendSymbol.apply(this, Array.prototype.slice.call(arguments, 1));
        } else {
            return proceed.apply(this, Array.prototype.slice.call(arguments, 1));
        }
    });
})(Highcharts);

Highcharts.setOptions({
    lang: {
        numericSymbols: [' K', ' M', ' B'],
    },
});

const getTickIntervalByProjectionYears = (years = 0) => {
    let yearsInterval = 1;

    if (years > 15) yearsInterval = 2;
    if (years > 32) yearsInterval = 5;
    if (years > 80) yearsInterval = 10;

    return yearsInterval * 365 * 24 * 60 * 60 * 1000;
};

export const getMonteCarloProjectionOptions = (params: IClientPackageMonteCarloParams): Options => ({
    chart: {
        animation: false,
        events: {
            load(this: Highcharts.Chart) {
                try {
                    params.onChartLoadEventHandlers.forEach((handler) => {
                        handler(this);
                    });
                } catch {
                    return;
                }
            },
            render: function (this: Highcharts.Chart) {
                const xAxis = this.xAxis[0] as Axis;
                // @ts-expect-error Problems with missing Highcharts types
                params.refs.xAxisYPosition.current = xAxis['bottom'] - xAxis['offset'];
                params.onChartRenderEventHandlers.forEach((handler: HighchartCallbackFn<Highcharts.Chart>) => {
                    handler(this);
                });
            } as ChartRenderCallbackFunction,
        },
        spacingLeft: 5,
        backgroundColor: '#fff',
    },
    accessibility: { enabled: false },
    credits: { enabled: false },
    title: {
        text: '-',
        style: {
            color: '#ffff',
        },
    },
    yAxis: {
        offset: CLIENT_OUTPUT_Y_AXIS_OFFSET,
        tickLength: 50,
        tickAmount: 10,
        gridLineColor: chartColors.GRID_LINE_CLIENT_OUTPUT,
        gridLineWidth: 2,
        maxPadding: 0.1,
        title: {
            text: '',
        },
        labels: {
            formatter: params.yAxisLabelsFormatterFn,
            style: {
                color: chartColors.LABEL_CLIENT_OUTPUT,
                fontSize: '2rem',
                fontFamily: FONT_FAMILY_VERLAG,
            },
            y: -10,
        },
    },
    xAxis: {
        crosshair: false,
        tickLength: 10,
        tickInterval: getTickIntervalByProjectionYears(params.projectionPeriodYears),
        className: 'highcharts-client-output-x-axis',
        startOnTick: false,
        offset: params.isClientOutput ? 0 : 2 * X_AXIS_OFFSET,
        labels: {
            formatter: params.xAxisLabelsFormatterFn,
            style: {
                color: chartColors.LABEL_CLIENT_OUTPUT,
                fontFamily: FONT_FAMILY_VERLAG,
                fontSize: '2.4rem',
                lineHeight: 48,
            },
            y: 40,
        },
        type: 'datetime',
        units: [['year', null]],
        min: moment(params.yearRange.from, 'yyyy').valueOf(),
        max: moment(params.yearRange.to, 'yyyy').valueOf(),
    },
    legend: {
        margin: 80,
        itemDistance: 40,
        symbolWidth: 35,
        symbolPadding: 20,
        verticalAlign: 'bottom',
        align: 'right',
        itemStyle: {
            fontSize: params.legendFontSize || '1rem',
            fontFamily: FONT_FAMILY_VERLAG,
        },
        labelFormatter(this: any) {
            return params.legendFormatterFn(this);
        },
    },
    series: params.seriesData,
    plotOptions: {
        series: {
            animation: {
                duration: 0,
            },
            marker: { enabled: false },
            pointStart: moment(params.yearRange.from).valueOf(),
            pointIntervalUnit: 'year',
            events: {
                legendItemClick: () => false,
            },
        },
    },
});
